/** ConfirmStatusModal.jsx - "mark this Won / Lost / Cancelled / No Quote?" confirmation; every one but Won asks why (11/3/26). */
import { useState } from 'react';
import { Icon } from '../ui/Icon';
import { Modal } from '../ui/Modal';
import { fmt } from '../../utils/format';
import { BTN_CANCEL, BTN_PRIMARY } from './editOpportunityConstants';

const PALETTE = {
  green: { bg: '#E8F5E9', border: '#C8E6C9', ink: '#1B5E20', sub: '#2E7D32', icon: 'check' },
  red: { bg: '#FFEBEE', border: '#FFCDD2', ink: '#B71C1C', sub: '#C62828', icon: 'x' },
  grey: { bg: '#F5F5F5', border: '#E0E0E0', ink: '#424242', sub: '#616161', icon: 'x' },
};

/**
 * `note` (optional) says what the status means, so Lost and Cancelled are not mixed up.
 * `askWhy`: a required note, handed to onConfirm(reason).
 */
export function ConfirmStatusModal({ opp, label, color, note, askWhy = false, onClose, onConfirm }) {
  const c = PALETTE[color] || PALETTE.red;
  const [why, setWhy] = useState('');
  const ready = !askWhy || why.trim() !== '';
  return (
    <Modal title={`Mark as ${label}`} onClose={onClose} width={480}>
      <div style={{ padding: 24 }}>
        <div style={{ background: c.bg, borderRadius: 8, padding: '14px 18px', marginBottom: 20, border: `1px solid ${c.border}` }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: c.ink, marginBottom: 4 }}>{opp.name}</div>
          <div style={{ fontSize: 11, color: c.sub }}>
            {opp.customer} · {fmt(opp.quotedPrice)}
          </div>
        </div>
        <div style={{ fontSize: 11, color: '#555', marginBottom: note ? 8 : 20, fontFamily: 'Montserrat' }}>
          This will update the project status to <strong>{label}</strong>.
        </div>
        {note && <div style={{ fontSize: 11, color: '#777', marginBottom: askWhy ? 12 : 20, fontFamily: 'Montserrat' }}>{note}</div>}
        {askWhy && (
          <label style={{ display: 'block', marginBottom: 20, fontFamily: 'Montserrat' }}>
            <span style={{ fontSize: 10.5, fontWeight: 800, color: '#333' }}>
              Why {label}? <span style={{ color: '#C62828' }}>*</span>
            </span>
            <textarea
              autoFocus
              value={why}
              onChange={(e) => setWhy(e.target.value)}
              rows={3}
              placeholder={label === 'No Quote' ? 'Why we did not quote it' : 'Who won it, price, timing, scope...'}
              style={{
                width: '100%',
                marginTop: 4,
                padding: 8,
                fontSize: 12,
                fontFamily: 'inherit',
                border: '1px solid #DDD',
                borderRadius: 6,
                boxSizing: 'border-box',
              }}
            />
          </label>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={BTN_CANCEL}>
            Cancel
          </button>
          <button
            onClick={() => ready && onConfirm(why.trim())}
            disabled={!ready}
            title={ready ? undefined : 'Add a note first'}
            style={{ ...BTN_PRIMARY, background: c.ink, opacity: ready ? 1 : 0.45, cursor: ready ? 'pointer' : 'not-allowed' }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Icon name={c.icon} size={12} color="#fff" /> Confirm {label}
            </span>
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** What Lost and Cancelled mean (Paul, Q5 / Q-01) - shown on their confirmations. */
export const LOST_NOTE = 'Lost means another company won the job. If it was cancelled or not awarded, use Cancelled.';
export const CANCELLED_NOTE = 'Cancelled means the job was cancelled or not awarded. If another company won it, use Lost.';
export const NO_QUOTE_NOTE = 'No Quote means we decided not to quote it.';
